import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { environment } from "../../../environments/environment";
import { GenericFilterRequestDto } from "../models/generic-filter-request.dto";
import { GenericSortRequestDto } from "../models/generic-sort-request.dto";
import { GenericResponseDto } from "../models/generic-response.dto";
import { ProofApprovalDto } from "../models/proof-approval/proof-approval.dto";
import { SendSelectedProofApprovalsResultDto } from "../models/proof-approval/send-selected-proof-approvals-result.dto";

/**
 * Service for managing proof-uploaded notifications via the Sales Portal proxy API.
 * All calls are proxied through JNI.Portal.Core.V2 → Nadel.Client.Portal.Core.
 */
@Injectable({ providedIn: "root" })
export class ProofApprovalService {
  private readonly baseUrl = `${environment.baseUrl}/ProofApproval`;

  constructor(private _http: HttpClient) {}

  /**
   * Lists pending and sent proof-uploaded notifications for administrators using server pagination.
   * @param skip Records to skip.
   * @param take Page size (server maximum 200).
   * @param filters Supported email, status, project and ID filters.
   * @param sort Server sort fields.
   * @returns Notification rows with the total matching record count.
   */
  listProofApprovals(
    skip: number,
    take: number,
    filters: GenericFilterRequestDto[] = [],
    sort: GenericSortRequestDto[] = []
  ): Observable<GenericResponseDto<ProofApprovalDto[]>> {
    return this._http
      .post<
        GenericResponseDto<ProofApprovalDto[]>
      >(`${this.baseUrl}/list`, { skip, take, filters, sort })
      .pipe(
        map(response => {
          if (response.status !== "success") {
            throw new Error(
              response.message || "Unable to load proof approvals."
            );
          }
          return response;
        })
      );
  }

  /**
   * Immediately sends a single pending proof-uploaded notification.
   * @param id Notification identifier.
   * @returns True only when the boolean success envelope confirms the send.
   */
  sendProofApproval(id: number): Observable<boolean> {
    return this._http
      .post<GenericResponseDto<boolean>>(`${this.baseUrl}/${id}/send`, {})
      .pipe(
        map(response => {
          if (response.status !== "success" || response.data !== true) {
            throw new Error(
              response.message || "Unable to send proof notification."
            );
          }
          return response.data;
        })
      );
  }

  /**
   * Immediately sends consolidated proof notification emails for a caller-selected set of pending
   * notifications, grouped by recipient email. One email is sent per unique recipient covering all of
   * their selected rows.
   * @param ids Notification identifiers to send.
   * @returns The sent, failed, and skipped notification identifiers.
   */
  sendSelectedProofApprovals(
    ids: number[]
  ): Observable<SendSelectedProofApprovalsResultDto> {
    return this._http
      .post<
        GenericResponseDto<SendSelectedProofApprovalsResultDto>
      >(`${this.baseUrl}/send-selected`, { ids })
      .pipe(
        map(response => {
          if (response.status !== "success") {
            throw new Error(
              response.message || "Unable to send proof notifications."
            );
          }
          return response.data;
        })
      );
  }

  /**
   * Collapses rows that belong to the same upload/resubmission event (`proofLogId`) on a line item
   * (`lineRecIdD365`) into a single row listing every recipient email, so each send event stays distinct.
   * The remaining columns come from the first row of each group; row order follows first occurrence.
   * @param rows Proof-uploaded notification records to group.
   * @returns One display row per upload/resubmission event with its distinct, alphabetically sorted recipient emails.
   */
  groupByRecipient(rows: ProofApprovalDto[]): ProofApprovalDto[] {
    const groups = new Map<string, Required<ProofApprovalDto>>();

    for (const row of rows) {
      // Recipients of the same upload/resubmission event (one proof log entry) share one row, so each
      // send event keeps its own history row even for repeated resubmissions of the same line item.
      const key = `${row.lineRecIdD365}|${row.isResubmission}|${row.proofLogId}`;
      const group = groups.get(key);

      if (!group) {
        groups.set(key, {
          ...row,
          ids: [row.id],
          emails: [row.email],
          extraEmails: [],
          emailSearch: row.email,
        });
        continue;
      }

      group.ids.push(row.id);
      if (
        !group.emails.some(e => e.toLowerCase() === row.email.toLowerCase())
      ) {
        group.emails.push(row.email);
      }
    }

    return [...groups.values()].map(group => {
      const emails = [...group.emails].sort((a, b) => a.localeCompare(b));
      return {
        ...group,
        email: emails[0],
        emails,
        extraEmails: emails.slice(1),
        emailSearch: emails.join(" "),
      };
    });
  }
}
